import { useCallback, useContext, useEffect, useMemo } from "react";
import { useChecksums } from "./useChecksums";
import LocationContext from "@jnctn/bridge/contexts/LocationContext";
import TenantContext from "@jnctn/bridge/contexts/TenantContext";
import { TenantPickerOptions } from "@jnctn/bridge/hooks/useTenantPicker";
import useTenantPickerWalletOption from "@jnctn/bridge/hooks/useTenantPickerWalletOption";

export default function useTenantPicker(
  onChangeTenantNavigation: () => void,
  defaultTenant?: string
) {
  const { tenant, setTenant } = useContext(TenantContext);

  useEffect(() => {
    if (defaultTenant) {
      setTenant(defaultTenant);
    }
  }, [defaultTenant, setTenant]);

  const checksums = useChecksums();

  const { setLocation } = useContext(LocationContext);

  const onChangeTenant = useCallback(
    (newTenant: string) => {
      if (newTenant !== tenant) {
        setLocation(undefined);
        setTenant(newTenant);
        onChangeTenantNavigation && onChangeTenantNavigation();
      }
    },
    [onChangeTenantNavigation, setLocation, setTenant, tenant]
  );

  const checksumOptions: TenantPickerOptions = useMemo(() => {
    if (typeof checksums?.map !== "function") {
      return null;
    }
    return checksums?.map(({ tenantName, isActive, profileComplete }) => {
      let suffix = "";
      if (!isActive && !profileComplete) {
        suffix = " - incomplete profile, inactive";
      } else if (!isActive) {
        suffix = " - inactive";
      } else if (!profileComplete) {
        suffix = " - incomplete profile";
      }

      return { key: tenantName, value: tenantName + suffix };
    });
  }, [checksums]);

  const options = useTenantPickerWalletOption(checksumOptions);

  return useMemo(
    () => ({ checksums, onChangeTenant, options, tenant }),
    [checksums, onChangeTenant, options, tenant]
  );
}
